import { useEffect, useRef, useState } from 'react'
import { Button } from '@/components/ui/button'
import { GTDialog, GTDialogBody, GTDialogFooter, GTDialogHeading } from '@/components/ui/gt-dialog'
import { PartyPopper } from 'lucide-react'
import { useGetGoalTaskLinks } from '../../services/api/goals.hooks'
import { useGetTasksV4 } from '../../services/api/tasks.hooks'
import { TaskHandlingChoices } from './ArchiveGoalDialog'
import { useGoalCreation } from './creation/shared/GoalCreationContext'
import { burstConfetti } from './goalMotion'
import { linkedTasksForGoal } from './goalTasks'
import { TGoalTaskHandling } from './goalTypes'

/** Achieving a goal is a bigger moment than meeting a week, so its burst is louder. */
const ACHIEVE_CONFETTI_SCALE = 3

/**
 * The achieve moment, opened from the details pane's "Goal achieved" and the weekly review's "Mark
 * achieved" so both produce the same goal. Nothing is lost — progress and completed tasks stay with
 * the goal — so the only question is what happens to its open tasks, asked with the same no-default
 * choice archive uses.
 *
 * The confirm waits for the server: confetti fires only once the goal has really moved, and a
 * failure leaves the dialog open with the choice intact so the user can retry.
 */
const AchieveGoalDialog = () => {
    const {
        allGoals,
        achieveGoalId,
        achieveGoalOptions,
        setAchieveGoalId,
        achieveGoal,
        isAchievingGoal,
        taskGoalLinks,
    } = useGoalCreation()
    const { data: allTasks } = useGetTasksV4()
    const { data: loadedTaskLinks } = useGetGoalTaskLinks()
    // Until both the tasks and their goal links have loaded, "no open tasks" is unknown, not zero:
    // confirming then would send "keep" for tasks the dialog never showed.
    const isTaskStateLoading = !allTasks || !loadedTaskLinks
    const goal = allGoals.find((g) => g.id === achieveGoalId)
    const [taskHandling, setTaskHandling] = useState<TGoalTaskHandling | null>(null)
    const [failed, setFailed] = useState(false)
    const confirmRef = useRef<HTMLButtonElement>(null)

    useEffect(() => {
        setTaskHandling(null)
        setFailed(false)
    }, [achieveGoalId])

    const openTasks = goal ? linkedTasksForGoal(goal.id, allTasks, taskGoalLinks).filter((t) => !t.is_done) : []
    const taskCount = openTasks.length
    const close = () => {
        if (!isAchievingGoal) setAchieveGoalId(null)
    }
    const confirm = () => {
        if (!goal || isAchievingGoal || isTaskStateLoading) return
        // With no open tasks there is nothing to decide, so "keep" is the only thing it can mean.
        const handling = taskCount === 0 ? 'keep' : taskHandling
        if (!handling) return
        setFailed(false)
        achieveGoal(goal.id, handling, {
            onAchieved: () => {
                if (confirmRef.current) burstConfetti(confirmRef.current, ACHIEVE_CONFETTI_SCALE)
                achieveGoalOptions?.onAchieved?.()
            },
            onError: () => {
                setFailed(true)
                achieveGoalOptions?.onError?.()
            },
        })
    }

    return (
        <GTDialog
            open={!!goal}
            onOpenChange={(next) => !next && close()}
            label="Goal achieved"
            description="Mark this goal achieved and choose what happens to its open tasks."
            onSubmit={confirm}
        >
            {goal && (
                <GTDialogBody className="gap-3">
                    <GTDialogHeading
                        title={`Mark “${goal.title}” achieved?`}
                        subtitle="It moves to Accomplished with its progress kept. You can reopen it any time."
                    />
                    {taskCount > 0 && (
                        <TaskHandlingChoices
                            label={`What happens to ${taskCount} open ${taskCount === 1 ? 'task' : 'tasks'}`}
                            taskCount={taskCount}
                            value={taskHandling}
                            onChange={setTaskHandling}
                        />
                    )}
                    {failed && (
                        <p className="text-body-sm text-destructive" role="alert">
                            Couldn’t save. Try again.
                        </p>
                    )}
                    <GTDialogFooter onCancel={close}>
                        <Button
                            ref={confirmRef}
                            size="sm"
                            className="gap-1.5 bg-success-hover text-white hover:bg-success-text active:scale-[0.96]"
                            onClick={confirm}
                            disabled={isAchievingGoal || isTaskStateLoading || (taskCount > 0 && !taskHandling)}
                        >
                            <PartyPopper className="size-4" aria-hidden />
                            {isAchievingGoal ? 'Saving…' : failed ? 'Retry' : 'Goal achieved'}
                        </Button>
                    </GTDialogFooter>
                </GTDialogBody>
            )}
        </GTDialog>
    )
}

export default AchieveGoalDialog
